import {
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { cn } from '../cn'
import { UiLink } from '../link'

export interface MenuItem {
  id: string
  label: ReactNode
  icon?: ReactNode
  /** Internal link target. */
  href?: string
  /** Full-page navigation (e.g. an API endpoint); bypasses the router. */
  external?: boolean
  onSelect?: () => void
  tone?: 'default' | 'danger'
}

export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>
  id: string
  type: 'button'
  'aria-haspopup': 'menu'
  'aria-expanded': boolean
  'aria-controls': string
  onClick: () => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
}

export interface MenuProps {
  /** Render the trigger button, spreading the given props onto it. */
  trigger: (props: MenuTriggerProps) => ReactNode
  items: MenuItem[]
  /** Non-interactive content above the items (signed-in user). */
  header?: ReactNode
  align?: 'start' | 'end'
  className?: string
}

/** Accessible menu button: arrow keys, Home/End, Escape returns focus, outside click closes. */
export function Menu({ trigger, items, header, align = 'end', className }: MenuProps) {
  const [open, setOpen] = useState(false)
  const baseId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLElement | null)[]>([])

  const focusItem = useCallback((index: number) => {
    const list = itemRefs.current.filter(Boolean) as HTMLElement[]
    const count = list.length
    if (count === 0) return
    list[((index % count) + count) % count]?.focus()
  }, [])

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  const initialIndex = useRef(0)

  useEffect(() => {
    if (!open) return
    focusItem(initialIndex.current)
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) close(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open, close, focusItem])

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      initialIndex.current = event.key === 'ArrowDown' ? 0 : -1
      setOpen(true)
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const list = itemRefs.current.filter(Boolean) as HTMLElement[]
    const index = list.indexOf(document.activeElement as HTMLElement)
    if (event.key === 'ArrowDown') focusItem(index + 1)
    else if (event.key === 'ArrowUp') focusItem(index - 1)
    else if (event.key === 'Home') focusItem(0)
    else if (event.key === 'End') focusItem(-1)
    else if (event.key === 'Escape') close(true)
    else if (event.key === 'Tab') close(false)
    else return
    if (event.key !== 'Tab') event.preventDefault()
  }

  function select(item: MenuItem) {
    close(!item.href)
    item.onSelect?.()
  }

  const itemClass = (item: MenuItem) =>
    cn(
      'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm no-underline outline-none focus-visible:bg-wash hover:bg-wash',
      item.tone === 'danger' ? 'text-danger' : 'text-ink',
    )

  itemRefs.current = []
  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {trigger({
        ref: triggerRef,
        id: `${baseId}-trigger`,
        type: 'button',
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': `${baseId}-menu`,
        onClick: () => {
          initialIndex.current = 0
          setOpen((value) => !value)
        },
        onKeyDown: onTriggerKeyDown,
      })}
      {open ? (
        <div
          id={`${baseId}-menu`}
          role="menu"
          aria-labelledby={`${baseId}-trigger`}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute top-full z-50 mt-2 w-64 animate-pop-in rounded-lg border border-line bg-surface p-1.5 shadow-raised',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {header ? (
            <div role="none" className="mb-1 border-b border-line px-2.5 pt-1.5 pb-2.5">
              {header}
            </div>
          ) : null}
          {items.map((item, index) => {
            const content = (
              <>
                {item.icon ? (
                  <span aria-hidden="true" className="text-sage [&>svg]:size-4">
                    {item.icon}
                  </span>
                ) : null}
                {item.label}
              </>
            )
            const ref = (node: HTMLElement | null) => {
              itemRefs.current[index] = node
            }
            if (item.href && item.external) {
              return (
                <a
                  key={item.id}
                  ref={ref}
                  role="menuitem"
                  tabIndex={-1}
                  href={item.href}
                  className={itemClass(item)}
                  onClick={() => select(item)}
                >
                  {content}
                </a>
              )
            }
            if (item.href) {
              return (
                <UiLink
                  key={item.id}
                  ref={ref}
                  role="menuitem"
                  tabIndex={-1}
                  href={item.href}
                  className={itemClass(item)}
                  onClick={() => select(item)}
                >
                  {content}
                </UiLink>
              )
            }
            return (
              <button
                key={item.id}
                ref={ref}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={itemClass(item)}
                onClick={() => select(item)}
              >
                {content}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
